<script setup lang="ts">
import Button from 'primevue/button'
import { Icon } from '@iconify/vue'
import { userRoleOptions } from '@/constants/user'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormDropdown from '@/components/inputs/FormDropdown.vue'
import { useUserForm } from '@/composables/useUserForm'
import type { UserResponse, User } from '@/types/user-types'
import { addUsers } from '@/api/users'

const emit = defineEmits<{
  (e: 'userAdded', newUser: UserResponse): void;
  (e: 'submissionFailed', errorDetail?: string): void;
  (e: 'cancel'): void;
}>()

const {
  values,
  errors,
  meta,
  handleSubmit,
  loading,
  showToast
} = useUserForm('add')

const submitUser = async (userData: User): Promise<UserResponse | null> => {
  loading.value = true
  try {
    const response: UserResponse = await addUsers(userData)
    showToast('success', 'Success', `User ${userData.firstName} ${userData.lastName} has been added successfully`)
    return response
  } catch (error) {
    console.error('Failed to add user:', error)
    showToast('error', 'Error', 'Failed to add user. Please try again.')
    return null
  } finally {
    loading.value = false
  }
}

const onSubmit = handleSubmit(async (formValues) => {
  try {
    const newUser = await submitUser(formValues)
    if (newUser) {
      emit('userAdded', newUser)
    } else {
      throw new Error('Failed to add user')
    }
  } catch (error: any) {
    emit('submissionFailed', error?.toString())
  }
})

const handleCancel = () => {
  emit('cancel')
}
</script>

<template>
  <form @submit.prevent="onSubmit" class="p-8 bg-white rounded-xl shadow-md">
    <div class="text-center mb-12">
      <h2 class="text-2xl font-semibold mb-4">Adding New User</h2>
      <p class="text-gray-600 mt-2">
        Once you complete the process, an email will be sent to the new <br />
        user with a link to set up their profile.
      </p>
      <div class="border-t border-gray-200 my-6"></div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 mb-4 gap-8">
      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">First Name</label>
        <FormInputText
          inputName="firstName"
          id="firstName"
          label=""
          v-model="values.firstName"
          :errorMessage="errors.firstName"
          placeholder="Enter user's first name"
          class="w-full"
        />
      </div>

      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">Last Name</label>
        <FormInputText
          inputName="lastName"
          id="lastName"
          label=""
          v-model="values.lastName"
          :errorMessage="errors.lastName"
          placeholder="Enter user's surname"
          class="w-full"
        />
      </div>

      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">Email Address</label>
        <FormInputText
          inputName="email"
          id="email"
          label=""
          v-model="values.email"
          :errorMessage="errors.email"
          placeholder="Enter user's email address here"
          class="w-full"
        />
      </div>

      <div class="flex flex-col">
        <label class="text-gray-700 mb-2">User Role</label>
        <FormDropdown
          inputName="userRole"
          id="userRole"
          v-model="values.userRole"
          :options="userRoleOptions"
          optionLabel="label"
          optionValue="value"
          :errorMessage="errors.userRole"
          placeholder="Choose user role"
          class="w-full"
        />
      </div>
    </div>
    <div class="flex justify-end gap-6 mt-10">
      <Button
        type="button"
        outlined
        class="px-8 py-2.5 border border-gray-300 text-gray-700"
        @click="handleCancel"
        :disabled="loading"
      >
        Cancel Process
      </Button>

      <Button
        v-ripple
        type="submit"
        class="px-8 py-2.5 flex items-center justify-center bg-orange-500 hover:bg-orange-600 text-white"
        :disabled="!meta.valid || loading"
        :loading="loading"
      >
        <span>Complete Process</span>
        <Icon v-if="!loading" icon="mdi:check-circle" class="text-xl ml-2" />
      </Button>
    </div>
  </form>
</template>

<style scoped>
:deep(.p-inputtext) {
  padding: 0.75rem 1rem;
}

:deep(.p-dropdown) {
  width: 90%;
}
</style>